import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Users,
  Search,
  Download,
  RefreshCw,
  Eye,
  TrendingUp,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { logoutAction } from "../../../store/slices/auth/authSliceActions";
import { getUsersAction } from "../../../store/slices/users/usersSliceActions";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";

const AdminOverview = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showUidModal, setShowUidModal] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { isAuthenticated, apiStatus: authApiStatus } = useAppSelector(
    (state) => state.auth
  );
  const { users, apiStatus } = useAppSelector((state) => state.users);

  const isLoading = apiStatus.getUsers === API_STATUS.STARTED;
  const isVerifying = authApiStatus.verify === API_STATUS.STARTED;
  const error = useAppSelector((state) => state.users.error);

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.uid?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle logout with Redux
  const handleLogout = useCallback(async () => {
    dispatch(logoutAction());
    navigate("/admin");
  }, [dispatch, navigate]);

  // Load users data
  const loadUsers = useCallback(() => {
    dispatch(getUsersAction());
  }, [dispatch]);

  // Check authentication and load data on component mount
  useEffect(() => {
    // Check if we have a stored token but aren't authenticated
    // This handles the case where the app just loaded with stored credentials
    const storedToken = localStorage.getItem("authToken");

    if (!isAuthenticated && !storedToken) {
      // No auth and no stored token - redirect to login
      navigate("/admin");
      return;
    }

    if (!isAuthenticated && storedToken && !isVerifying) {
      // We have a token but not authenticated, and not currently verifying
      // This might happen if verification hasn't completed yet, so wait
      return;
    }

    if (isAuthenticated) {
      // Load users if not already loaded and we're authenticated
      if (users.length === 0) {
        loadUsers();
      }
    }
  }, [isAuthenticated, navigate, users.length, loadUsers, isVerifying]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Export to CSV
  const exportToCSV = () => {
    const csvContent = [
      [
        "UID",
        "Email",
        "Name",
        "Country",
        "Provider",
        "Plan",
        "Expiration Date",
        "Created At",
      ].join(","),
      ...filteredUsers.map((user) =>
        [
          user.uid || user.id,
          user.email,
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.name || "N/A",
          user.country || "Not specified",
          user.provider || "email",
          user.subscriptionStatus,
          user.subscriptionExpirationDate || "N/A",
          user.createdAt || "N/A",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users_export.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const containerStyle = {
    minHeight: "calc(100vh - 80px)",
    backgroundColor: colors.extraLightGray,
    padding: "2rem",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: colors.white,
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    flexWrap: "wrap",
    gap: "1rem",
  };

  const titleStyle = {
    fontSize: "2rem",
    fontWeight: "700",
    color: colors.darkGreen,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    minWidth: "200px",
  };

  const actionButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.3s ease",
    marginLeft: "0.5rem",
    whiteSpace: "nowrap",
  };

  const searchContainerStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const searchInputStyle = {
    width: "100%",
    padding: "1rem 3rem 1rem 3rem",
    border: `2px solid ${colors.doubleExtraLightGray}`,
    borderRadius: "8px",
    fontSize: "1rem",
    outline: "none",
  };

  const tableContainerStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    overflow: "auto",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    WebkitOverflowScrolling: "touch",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  };

  const thStyle = {
    backgroundColor: colors.darkGreen,
    color: colors.white,
    padding: "1rem",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const tdStyle = {
    padding: "1rem",
    borderBottom: `1px solid ${colors.doubleExtraLightGray}`,
    fontSize: "0.875rem",
  };

  const providerBadgeStyle = (provider) => ({
    padding: "0.25rem 0.75rem",
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontWeight: "600",
    backgroundColor:
      provider === "google"
        ? "#e3f2fd"
        : provider === "apple"
        ? "#f3e5f5"
        : "#f5f5f5",
    color:
      provider === "google"
        ? "#1976d2"
        : provider === "apple"
        ? "#7b1fa2"
        : "#666",
  });

  if (!sessionStorage.getItem("adminAuthenticated")) {
    return null; // Component will redirect
  }

  const openUidModal = (uid) => {
    if (!uid) return;
    setSelectedUid(uid);
    setShowUidModal(true);
  };

  const closeUidModal = () => {
    setShowUidModal(false);
    setSelectedUid(null);
  };

  // Show loading state while verifying token
  if (isVerifying) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.extraLightGray,
        }}
      >
        <div
          style={{
            backgroundColor: colors.white,
            padding: "3rem",
            borderRadius: "12px",
            boxShadow: "0 2px 20px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <RefreshCw
            size={32}
            style={{
              color: colors.darkGreen,
              animation: "spin 1s linear infinite",
              marginBottom: "1rem",
            }}
          />
          <p
            style={{
              margin: 0,
              color: colors.darkGray,
              fontSize: "1.1rem",
              fontWeight: "500",
            }}
          >
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} className="admin-responsive-container">
      <div style={headerStyle} className="admin-header-responsive">
        <h1 style={titleStyle} className="admin-title">
          <Users size={32} />
          Admin Dashboard
        </h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
          className="admin-button-group"
        >
          <button
            onClick={loadUsers}
            style={{
              ...actionButtonStyle,
              backgroundColor: colors.lightBlue,
              color: colors.darkBlue,
            }}
            disabled={isLoading}
          >
            <RefreshCw size={18} />
            {isLoading ? "Loading..." : "Refresh"}
          </button>
          <button
            onClick={() => navigate("/admin/analytics")}
            style={{
              ...actionButtonStyle,
              backgroundColor: "#f0f9ff",
              color: "#0369a1",
            }}
          >
            <TrendingUp size={18} />
            Analytics
          </button>
          <button
            onClick={exportToCSV}
            style={{
              ...actionButtonStyle,
              backgroundColor: colors.lightGreen,
              color: colors.darkGreen,
            }}
          >
            <Download size={18} />
            Export CSV
          </button>
          <button
            onClick={handleLogout}
            style={{
              ...actionButtonStyle,
              backgroundColor: "#fee2e2",
              color: "#dc2626",
            }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      <div style={searchContainerStyle}>
        <div style={{ position: "relative" }}>
          <Search
            size={20}
            style={{
              position: "absolute",
              left: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.darkGray,
              zIndex: 1,
            }}
          />
          <input
            type="text"
            placeholder="Search users by email, name, or country..."
            value={searchTerm}
            onChange={handleSearch}
            style={searchInputStyle}
            className="admin-search-input"
          />
        </div>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: "#fee2e2",
            color: "#dc2626",
            padding: "1rem",
            borderRadius: "8px",
            marginBottom: "2rem",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      <div style={tableContainerStyle} className="admin-table-container">
        {isLoading ? (
          <div
            style={{
              padding: "3rem",
              textAlign: "center",
              color: colors.darkGray,
            }}
          >
            Loading users...
          </div>
        ) : (
          <table style={tableStyle} className="admin-table">
            <thead>
              <tr>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle} className="hide-mobile">
                  Country
                </th>
                <th style={thStyle} className="hide-mobile">
                  Provider
                </th>
                <th style={thStyle}>Plan</th>
                <th style={thStyle} className="hide-mobile">
                  Workout
                </th>
                <th style={thStyle} className="hide-mobile">
                  Expiration Date
                </th>
                <th style={thStyle} className="hide-mobile">
                  Created
                </th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr
                    key={user.uid}
                    style={{
                      "&:hover": { backgroundColor: colors.extraLightGray },
                    }}
                  >
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              backgroundColor: colors.darkGreen,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: colors.white,
                              fontWeight: "600",
                            }}
                          >
                            {(() => {
                              // Get the name to display
                              let displayName = user.displayName || user.name;

                              // If no displayName and fullName is an object, construct it
                              if (
                                !displayName &&
                                user.fullName &&
                                typeof user.fullName === "object"
                              ) {
                                displayName = user.fullName.givenName || "User";
                              }

                              // Fallback to 'User' if still no name
                              displayName = displayName || "User";

                              return typeof displayName === "string"
                                ? displayName.charAt(0).toUpperCase()
                                : "U";
                            })()}
                          </div>
                        )}
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              color: colors.darkBlue,
                              cursor: "pointer",
                              textDecoration: "underline",
                              textDecorationStyle: "dotted",
                            }}
                            onClick={() => openUidModal(user.uid)}
                          >
                            {(() => {
                              // Get the display name
                              let displayName = user.displayName || user.name;

                              // If no displayName and fullName is an object, construct it
                              if (
                                !displayName &&
                                user.fullName &&
                                typeof user.fullName === "object"
                              ) {
                                const { givenName, familyName } = user.fullName;
                                displayName =
                                  [givenName, familyName]
                                    .filter(Boolean)
                                    .join(" ") || "User";
                              }

                              return displayName || "Unknown User";
                            })()}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                              cursor: user.uid ? "pointer" : "default",
                            }}
                            onClick={() => openUidModal(user.uid)}
                            title={user.uid}
                          >
                            ID:{" "}
                            {user.uid
                              ? user.uid.substring(0, 8) + "..."
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>{user.email || "N/A"}</td>
                    <td style={tdStyle} className="hide-mobile">
                      {user.country || "N/A"}
                    </td>
                    <td style={tdStyle} className="hide-mobile">
                      <span style={providerBadgeStyle(user.provider)}>
                        {(() => {
                          const provider = user.provider;
                          if (
                            typeof provider === "string" &&
                            provider.length > 0
                          ) {
                            return (
                              provider.charAt(0).toUpperCase() +
                              provider.slice(1)
                            );
                          }
                          return "Unknown";
                        })()}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span>{user.subscriptionStatus || "N/A"}</span>
                    </td>
                    <td style={tdStyle} className="hide-mobile">
                      <span
                        style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "20px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          backgroundColor: user.hasWorkoutPlan
                            ? "#dcfce7"
                            : "#fee2e2",
                          color: user.hasWorkoutPlan ? "#166534" : "#dc2626",
                        }}
                      >
                        {user.hasWorkoutPlan ? "Yes" : "No"}
                      </span>
                    </td>
                    <td style={tdStyle} className="hide-mobile">
                      {user.subscriptionExpirationDate ? (
                        <div style={{ fontSize: "0.75rem" }}>
                          <div
                            style={{
                              fontWeight: "600",
                              color: colors.darkBlue,
                            }}
                          >
                            {new Date(
                              user.subscriptionExpirationDate
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                          <div
                            style={{
                              color: colors.darkGray,
                              fontSize: "0.65rem",
                            }}
                          >
                            {new Date(
                              user.subscriptionExpirationDate
                            ).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      ) : (
                        <span
                          style={{
                            color: colors.darkGray,
                            fontSize: "0.75rem",
                          }}
                        >
                          -
                        </span>
                      )}
                    </td>
                    <td style={tdStyle} className="hide-mobile">
                      {user.createdAt || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => navigate(`/admin/users/${user.uid}`)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "0.5rem 1rem",
                          backgroundColor: colors.lightBlue,
                          color: colors.darkBlue,
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          transition: "all 0.2s ease",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = colors.darkBlue;
                          e.target.style.color = colors.white;
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = colors.lightBlue;
                          e.target.style.color = colors.darkBlue;
                        }}
                      >
                        <Eye size={16} />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    style={{ ...tdStyle, textAlign: "center", padding: "3rem" }}
                  >
                    No users found matching your search criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div
        style={{
          marginTop: "2rem",
          textAlign: "center",
          color: colors.darkGray,
          fontSize: "0.875rem",
        }}
      >
        Total Users: {filteredUsers.length} | Showing: {filteredUsers.length}
      </div>
      {showUidModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeUidModal}
        >
          <div
            style={{
              background: colors.white,
              padding: "2rem",
              borderRadius: "12px",
              width: "min(90%, 480px)",
              boxShadow: "0 10px 25px -5px rgba(0,0,0,0.2)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 1rem 0",
                fontSize: "1.25rem",
                fontWeight: 700,
                color: colors.darkBlue,
              }}
            >
              Full User ID
            </h3>
            <div
              style={{
                fontFamily: "monospace",
                background: colors.extraLightGray,
                padding: "1rem",
                borderRadius: "8px",
                wordBreak: "break-all",
                fontSize: "0.9rem",
                position: "relative",
              }}
            >
              {selectedUid}
              <button
                onClick={() => {
                  if (selectedUid) {
                    navigator.clipboard.writeText(selectedUid);
                  }
                }}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: colors.darkBlue,
                  color: colors.white,
                  border: "none",
                  padding: "0.25rem 0.5rem",
                  borderRadius: 6,
                  fontSize: "0.65rem",
                  cursor: "pointer",
                  letterSpacing: 0.5,
                  fontWeight: 600,
                }}
              >
                COPY
              </button>
            </div>
            <div
              style={{ marginTop: "1.25rem", display: "flex", gap: "0.75rem" }}
            >
              <button
                onClick={closeUidModal}
                style={{
                  flex: 1,
                  background: colors.lightGray,
                  border: "none",
                  padding: "0.75rem 1rem",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  if (selectedUid) navigator.clipboard.writeText(selectedUid);
                  closeUidModal();
                }}
                style={{
                  flex: 1,
                  background: colors.darkGreen,
                  color: colors.white,
                  border: "none",
                  padding: "0.75rem 1rem",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Copy & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOverview;
